import { Badge, Box, Button, Collapsible, Heading, HStack, Stack, Text } from "@chakra-ui/react";
import { LuChevronDown } from "react-icons/lu";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "../components/DataTable";
import { actionLabel, formatAuditTime, StepName } from "../components/auditDisplay";
import type { AssignmentDetail } from "./ActivityDetailParts";

// Derived, not described: a hand-written shape is how this page's other types
// drifted from the query before.
export type HistoryEntry = AssignmentDetail["history"][number];

// What the query returns at most; the newest entries, newest first.
const HISTORY_LIMIT = 100;

const column = createColumnHelper<HistoryEntry>();

const COLUMNS = [
  column.accessor("createdAt", {
    header: "When",
    cell: ({ getValue }) => <Text whiteSpace="nowrap">{formatAuditTime(getValue())}</Text>,
  }),
  column.accessor("actorName", {
    header: "Who",
    cell: ({ row }) => (
      <HStack gap={2} whiteSpace="nowrap">
        <Text color={row.original.actorName === "CeMMbase" ? "fg.muted" : undefined}>
          {row.original.actorName}
        </Text>
        {/* Shown to everybody who can open the activity, deliberately: if an
            admin acted as you, your own history is where you should learn it. */}
        {row.original.actedAsName === null ? null : (
          <Badge colorPalette="orange" variant="subtle">as {row.original.actedAsName}</Badge>
        )}
      </HStack>
    ),
  }),
  column.accessor("action", {
    header: "Act",
    // The module is always this activity here, so only the verb.
    cell: ({ getValue }) => <Text whiteSpace="nowrap">{actionLabel(getValue()).verb}</Text>,
  }),
  column.display({
    id: "what",
    header: "What",
    // Wraps rather than truncating, unlike the audit log: this is where
    // somebody comes to read WHY their deadline moved, so the reason is the
    // point and must be readable in full.
    cell: ({ row }) => {
      const { part, detail, reason } = row.original;
      return (
        <Text>
          {part === null ? null : (
            <>
              <StepName title={part} />
              {": "}
            </>
          )}
          {detail}
          {reason === "" ? null : (
            <Text as="span" color="fg.muted">{` · Reason: ${reason}`}</Text>
          )}
        </Text>
      );
    },
  }),
];

/**
 * Everything recorded about one activity, newest first (cicd-backlog §P14).
 *
 * Visible to whoever can open the activity: having access to the activity is
 * having access to its history. It is the audit log read by this activity, so
 * an act shows here exactly as it does there.
 */
export function ActivityHistory({ history }: { history: HistoryEntry[] }) {
  // Closed: it is where somebody comes to find out why something changed,
  // not something to read on every visit.
  return (
    <Collapsible.Root as="section">
      <Collapsible.Trigger asChild>
        <Button variant="plain" px={0} gap={2}>
          <Heading size="xl">History</Heading>
          <Text as="span" fontSize="sm" color="fg.muted">({history.length}{history.length >= HISTORY_LIMIT ? "+" : ""})</Text>
          <Collapsible.Indicator transition="transform 0.15s" _open={{ transform: "rotate(180deg)" }}>
            <LuChevronDown />
          </Collapsible.Indicator>
        </Button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Stack gap={3} pt={2}>
          <Text fontSize="sm" color="fg.muted">
            Newest first: who did what, and why.
            {history.length >= HISTORY_LIMIT ? ` Showing the latest ${HISTORY_LIMIT}.` : ""}
          </Text>
          <Box borderWidth="1px" borderColor="border" bg="bg">
            <DataTable
              data={history}
              columns={COLUMNS}
              getRowId={(entry) => entry._id}
              emptyMessage="Nothing recorded yet."
              size="sm"
              interactive={false}
            />
          </Box>
        </Stack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
